import React, {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Alert} from '~/components/Alert';
import {Breadcrumbs} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {InfoPopover} from '~/components/InfoPopover';
import {Pagination} from '~/components/Pagination';
import {Progress} from '~/components/Progress';
import {IColumn, Table} from '~/components/Table';
import {Title} from '~/components/Title';
import {getApiErrorMessage} from '~/lib/errors/apiError';
import {Intent} from '~/lib/intent';
import {fetchSellersJsonDiff} from '~/modules/Clients/api';
import {isEmptyDiff, ISellerJsonEntry, ISellersJsonEntryChange} from '~/modules/Clients/models/SellersJson';
import style from './SellersJson.module.scss';

const PAGE_HELP = 'sellers.json is what a buyer verifies a supply chain against: it reads the sid from the chain and '
    + 'looks that identifier up in our file. The file is written one entry at a time, as sources are created, renamed '
    + 'and deactivated, and nothing recomputes it as a whole — so it drifts from what the exchange would publish '
    + 'today. This page shows the drift. It changes nothing.';

const PUBLISHED_HELP = 'Records in the file as it is served to buyers right now.';

const COMPUTED_HELP = 'Records the exchange would publish if the file were assembled from scratch this second, by the '
    + 'same rule that has always assembled it. Reading this page writes nothing.';

const ADDED_HELP = 'The exchange would publish it, the file does not carry it. A buyer looking one of these up today '
    + 'finds nothing, so a chain signed with it fails verification.';

const WITHDRAWN_HELP = 'In the file, no longer produced — an inactive source or seller, or a domain row that is gone. '
    + 'The file still makes a claim the exchange no longer makes.';

const CHANGED_HELP = 'Same seller_id, different name, domain or seller_type. The identifier a buyer already knows '
    + 'stays; what the file says about it moves.';

const PAGE_SIZE = 25;

const PagedEntries = <T,>({records, columns}: {records: T[]; columns: IColumn<T>[]}) => {
    const [paginator, setPaginator] = useState({page: 0, pageSize: PAGE_SIZE});
    const page = paginator.page * paginator.pageSize < records.length ? paginator.page : 0;
    const offset = page * paginator.pageSize;

    return <>
        <Table records={records.slice(offset, offset + paginator.pageSize)} columns={columns} header/>
        {records.length > paginator.pageSize && <Pagination
            padding={4}
            offset={offset}
            limit={paginator.pageSize}
            total={records.length}
            onChange={(pageNo, perPage) => setPaginator({page: pageNo, pageSize: perPage})}
        />}
    </>;
};

const moved = (change: ISellersJsonEntryChange, name: string, field: (entry: ISellerJsonEntry) => string) => {
    const was = field(change.published);
    const now = field(change.computed);

    return was === now
        ? now
        : <span className={style.moved}>
            <span className={style.was}>{was || `no ${name}`}</span>
            <span className={style.arrow} aria-label="becomes">→</span>
            <span>{now || `no ${name}`}</span>
        </span>;
};

const withdrawnColumns: IColumn<ISellerJsonEntry>[] = [
    {header: 'seller_id', cell: (entry: ISellerJsonEntry) => <s className={style.was}>{entry.seller_id}</s>},
    {header: 'name', cell: (entry: ISellerJsonEntry) => <s className={style.was}>{entry.name}</s>},
    {header: 'domain', cell: (entry: ISellerJsonEntry) => <s className={style.was}>{entry.domain}</s>},
    {header: 'seller_type', cell: (entry: ISellerJsonEntry) => <s className={style.was}>{entry.seller_type}</s>}
];

const entryColumns: IColumn<ISellerJsonEntry>[] = [
    {field: 'seller_id' as const, header: 'seller_id'},
    {field: 'name' as const, header: 'name'},
    {field: 'domain' as const, header: 'domain'},
    {field: 'seller_type' as const, header: 'seller_type'}
];

export const SellersJsonView = () => {
    const diffQuery = useQuery({queryKey: ['sellers-json-diff'], queryFn: fetchSellersJsonDiff});

    const diff = diffQuery.data;

    const section = (title: string, help: string, entries: ISellerJsonEntry[], columns = entryColumns) =>
        entries.length > 0 && <div className={style.block}>
            <h3 className={style.blockTitle}>{title} ({entries.length}) <InfoPopover content={help}/></h3>
            <PagedEntries records={entries} columns={columns}/>
        </div>;

    const changes = (entries: ISellersJsonEntryChange[]) => entries.length > 0 && <div className={style.block}>
        <h3 className={style.blockTitle}>Changed ({entries.length}) <InfoPopover content={CHANGED_HELP}/></h3>
        <PagedEntries
            records={entries}
            columns={[
                {header: 'seller_id', cell: (change: ISellersJsonEntryChange) => change.published.seller_id},
                {header: 'domain', cell: (change: ISellersJsonEntryChange) => moved(change, 'domain', e => e.domain)},
                {header: 'name', cell: (change: ISellersJsonEntryChange) => moved(change, 'name', e => e.name)},
                {
                    header: 'seller_type',
                    cell: (change: ISellersJsonEntryChange) => moved(change, 'seller_type', e => e.seller_type)
                }
            ]}
        />
    </div>;

    return <div>
        <div className="page-header-sticky">
            <div className="page-header-breadcrumbs"><Breadcrumbs/></div>
            <Title className="page-header-title" toolbar={
                <Button responsive intent={Intent.SUCCESS} disabled={diffQuery.isFetching}
                    leftIcon={<i className='fa fa-refresh'/>}
                    onClick={() => diffQuery.refetch()}
                >Refresh</Button>
            }>sellers.json review <InfoPopover content={PAGE_HELP} width={420}/></Title>
        </div>
        <div className="layout-content-box">
            {diffQuery.isError && <Alert
                type="danger"
                alertText={getApiErrorMessage(diffQuery.error, 'Failed to read the published file')}
            />}

            {diffQuery.isPending ? <Progress/> : diff && <>
                <div className={style.summary}>
                    <span>{diff.publishedCount} published <InfoPopover content={PUBLISHED_HELP}/></span>
                    <span>{diff.computedCount} from the database <InfoPopover content={COMPUTED_HELP}/></span>
                    {isEmptyDiff(diff) &&
                        <span className={style.agreed}>The file says what the exchange would publish.</span>}
                </div>

                {section('Added', ADDED_HELP, diff.added)}
                {section('Withdrawn', WITHDRAWN_HELP, diff.removed, withdrawnColumns)}
                {changes(diff.changed)}
            </>}
        </div>
    </div>;
};
